/**
 * The due & overdue list's view rules — which rows show and in what order —
 * kept out of the page so they are one readable place and can be checked
 * without a DOM.
 *
 * The server still decides the expensive cuts (the look-ahead window, who the
 * work is allocated to, "due before", overdue only). Everything here works on
 * the rows already loaded: obligation type, status, a client search, the
 * "due soon" tab and the sort. All of it lives in the URL, like the server
 * filters, so a filtered view can still be sent to someone.
 */

import type { Allocation, ReportItem } from './api'
import { statusStyle } from './status'

export type DueView = 'all' | 'overdue' | 'soon'
export type SortKey = 'client' | 'item' | 'due' | 'allocated' | 'status'
export type SortDir = 'asc' | 'desc'

export interface ListFilters {
  view: DueView
  /** Item codes to keep; empty = every type. */
  types: string[]
  /** Status values to keep; empty = every status. */
  statuses: string[]
  /** Case-insensitive client-name search; blank = no search. */
  query: string
}

export interface ListSort {
  key: SortKey
  dir: SortDir
}

export const DEFAULT_SORT: ListSort = { key: 'due', dir: 'asc' }
const SORT_KEYS: readonly SortKey[] = ['client', 'item', 'due', 'allocated', 'status']

/** A comma list in the URL -> distinct, non-blank values. */
export function readList(raw: string | null): string[] {
  if (raw === null) return []
  return [...new Set(raw.split(',').map((value) => value.trim()).filter((value) => value !== ''))]
}

/**
 * `show=overdue|soon`. The older `overdue=1` link still means "overdue", so a
 * view someone saved before this change opens the same way.
 */
export function readView(params: URLSearchParams): DueView {
  const show = params.get('show')
  if (show === 'overdue' || show === 'soon') return show
  if (params.get('overdue') === '1') return 'overdue'
  return 'all'
}

export function readSort(params: URLSearchParams): ListSort {
  const key = params.get('sort')
  const dir = params.get('dir')
  return {
    key: SORT_KEYS.includes(key as SortKey) ? (key as SortKey) : DEFAULT_SORT.key,
    dir: dir === 'desc' ? 'desc' : 'asc',
  }
}

/** Clicking a column: a new column sorts ascending; the same column flips. */
export function nextSort(current: ListSort, key: SortKey): ListSort {
  if (current.key !== key) return { key, dir: 'asc' }
  return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
}

/** True when any filter that only this page applies is narrowing the rows. */
export function hasListFilters(filters: ListFilters): boolean {
  return (
    filters.view === 'soon' ||
    filters.types.length > 0 ||
    filters.statuses.length > 0 ||
    filters.query.trim() !== ''
  )
}

export function applyListFilters(items: readonly ReportItem[], filters: ListFilters): ReportItem[] {
  const query = filters.query.trim().toLocaleLowerCase()
  const types = new Set(filters.types)
  const statuses = new Set(filters.statuses)
  return items.filter(
    (item) =>
      // Due today is not overdue (daysOverdue 0), so it belongs to "due soon".
      (filters.view !== 'soon' || !item.isOverdue) &&
      (filters.view !== 'overdue' || item.isOverdue) &&
      (types.size === 0 || types.has(item.itemCode)) &&
      (statuses.size === 0 || statuses.has(item.status)) &&
      (query === '' || item.clientName.toLocaleLowerCase().includes(query))
  )
}

/**
 * Workflow order rather than alphabetical, so "status" sorts the way the work
 * flows. A status this list does not know sorts last, by its raw value.
 */
const STATUS_ORDER = [
  'not_started',
  'in_progress',
  'sent_to_client',
  'awaiting_signature',
  'signed',
  'for_review',
  'on_hold',
  'not_applicable',
  'completed',
]

function statusRank(status: string): number {
  const index = STATUS_ORDER.indexOf(status)
  return index === -1 ? STATUS_ORDER.length : index
}

/** Unallocated last and unmatched initials just before it, whichever way the column sorts. */
function allocationRank(allocation: Allocation | null): number {
  if (allocation === null) return 2
  return allocation.source === 'unmatched' ? 1 : 0
}

const text = (a: string, b: string): number => a.localeCompare(b, 'en-ZA', { sensitivity: 'base' })

function compareBy(key: SortKey, a: ReportItem, b: ReportItem): number {
  switch (key) {
    case 'client':
      return text(a.clientName, b.clientName)
    case 'item':
      return text(a.itemCode, b.itemCode)
    case 'due':
      // 'YYYY-MM-DD' compares correctly as text.
      return a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0
    case 'allocated':
      return text(a.allocation?.name ?? '', b.allocation?.name ?? '')
    case 'status':
      return statusRank(a.status) - statusRank(b.status) || text(a.status, b.status)
  }
}

/**
 * Stable sort on the chosen column; ties fall back to the server's own order
 * (due date, then client), always ascending, so equal rows never shuffle when
 * the direction flips.
 */
export function sortItems(items: readonly ReportItem[], sort: ListSort): ReportItem[] {
  const sign = sort.dir === 'asc' ? 1 : -1
  return [...items].sort((a, b) => {
    if (sort.key === 'allocated') {
      const rank = allocationRank(a.allocation) - allocationRank(b.allocation)
      if (rank !== 0) return rank
    }
    return (
      sign * compareBy(sort.key, a, b) ||
      compareBy('due', a, b) ||
      compareBy('client', a, b) ||
      compareBy('item', a, b)
    )
  })
}

export interface FilterOption {
  value: string
  label: string
  hint?: string
  count: number
}

/** The obligation and job types present in the loaded rows, with how many of each. */
export function typeOptions(items: readonly ReportItem[]): FilterOption[] {
  const byCode = new Map<string, FilterOption>()
  for (const item of items) {
    const option = byCode.get(item.itemCode)
    if (option) option.count += 1
    else
      byCode.set(item.itemCode, {
        value: item.itemCode,
        label: typeLabel(item.itemDescription),
        hint: item.itemCode,
        count: 1,
      })
  }
  return [...byCode.values()].sort((a, b) => text(a.label, b.label))
}

/** The statuses present in the loaded rows, in workflow order. */
export function statusOptions(items: readonly ReportItem[]): FilterOption[] {
  const counts = new Map<string, number>()
  for (const item of items) counts.set(item.status, (counts.get(item.status) ?? 0) + 1)
  return [...counts.entries()]
    .sort(([a], [b]) => statusRank(a) - statusRank(b) || text(a, b))
    .map(([value, count]) => ({ value, label: statusStyle(value).label, count }))
}

/**
 * The reference data spells a dash as "--" ("Provisional tax -- First
 * period"). Shown to a person, that is an en dash. The stored text is left
 * alone.
 */
export function typeLabel(description: string): string {
  return description.replace(/\s+--\s+/g, ' – ')
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** 'YYYY-MM-DD' -> "Fri 7 Feb 2025". Read as a calendar date, never through a clock. */
export function longDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (match === null) return iso
  const [, y, m, d] = match
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)))
  return `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

/** 624 -> "about 20 months"; only used where days alone get hard to read. */
function roughSpan(days: number): string | null {
  if (days < 60) return null
  if (days < 730) return `about ${Math.round(days / 30.44)} months`
  const years = Math.round((days / 365.25) * 10) / 10
  return `about ${years} years`
}

/** The due date said in full, for the tooltip on the due column. */
export function dueExplanation(dueDate: string, daysOverdue: number): string {
  const due = `Due ${longDate(dueDate)}`
  if (daysOverdue === 0) return `${due} — today.`
  const days = Math.abs(daysOverdue)
  const span = roughSpan(days)
  const amount = `${days} day${days === 1 ? '' : 's'}${span === null ? '' : ` (${span})`}`
  return daysOverdue > 0 ? `${due} — ${amount} ago.` : `${due} — in ${amount}.`
}
